import { zodResolver } from "@hookform/resolvers/zod";
import { useDocumentTitle } from "@uidotdev/usehooks";
import { format, subYears } from "date-fns";
import { ChangeEvent, useEffect, useMemo } from "react";
import { usePlacesWidget } from "react-google-autocomplete";
import { useForm } from "react-hook-form";
import { parseGooglePlace } from "utils/google-places";
import { z } from "zod";

import { StandardContent } from "@/components/App";
import { useAuth } from "@/components/Auth";
import Button from "@/components/Button";
import ErrorText from "@/components/ErrorText";
import FormField from "@/components/FormField";
import FormFields from "@/components/FormFields";
import Header from "@/components/Header";
import { Input, Select } from "@/components/Input";
import Label from "@/components/Label";
import Loader from "@/components/Loader";
import { useNotification } from "@/components/Notification";
import { ErrorMessages } from "@/constants/messages";
import { PageTitle } from "@/constants/pages";
import { needsGuardian } from "@/domain/needsGuardian";
import { usePageEventTracking } from "@/hooks/event-tracking";
import { useUpdateUserMutation } from "@/hooks/user";
import { UserInfo } from "@/services/user";
import { isSetAndNotEmpty } from "@/utils/helpers";

const schema = z.object({
  first_name: z.string().min(1, { message: "First name is required" }),
  last_name: z.string().min(1, { message: "Last name is required" }),
  address: z.string().min(1, { message: "Address is required" }),
  address2: z.string().nullable(),
  city: z.string().min(1, { message: "City is required" }),
  state: z.string().min(1, { message: "State is required" }),
  postal_code: z.string().min(1, { message: "Zip is required" }),
  emergency_name: z
    .string()
    .min(1, { message: "Emergency Contact Name is required" }),
  emergency_phone: z.string().min(10, {
    message: "10 digit emergency contact phone number is required",
  }),
  mobile_phone: z.string().min(10, {
    message: "10 digit emergency contact phone number is required",
  }),
  gender: z.string().min(1, { message: "Gender is required" }),
  dob: z.string().min(1, { message: "Date of Birth is required" }),
  guardianName: z.string().optional(),
  guardianPhone: z.string().optional(),
});

const minDOB = format(subYears(new Date(), 120), "yyyy-MM-dd");
const maxDOB = format(new Date(), "yyyy-MM-dd");

type Schema = z.infer<typeof schema>;

type Props = {
  purpose?: "profile" | "waiver";
};

export default function ProfilePage(props: Props) {
  return <LoadCustomerAndGuardian {...props} />;
}

function LoadCustomerAndGuardian(props: Props) {
  const { userQuery } = useAuth();

  if (userQuery.isPending) {
    return <Loader />;
  }

  if (userQuery.isError) {
    return <ErrorText error={ErrorMessages.GENERIC} center />;
  }

  return <Content user={userQuery.data} {...props} />;
}

function Content(props: { user: UserInfo; purpose?: "profile" | "waiver" }) {
  const { user } = props;
  const { waiver, hasIncompletedAccountInformation } = useAuth();
  const notify = useNotification();
  const {
    handleSubmit,
    register,
    setValue,
    watch,
    setError,
    clearErrors,
    formState: { isValid, errors },
  } = useForm<Schema>({
    mode: "onBlur",
    resolver: zodResolver(schema),
    defaultValues: {
      first_name: user.first_name ?? "",
      last_name: user.last_name ?? "",
      address: user.address ?? "",
      city: user.city ?? "",
      state: user.state ?? "",
      postal_code: user.postal_code ?? "",
      emergency_name: user.emergency_name ?? "",
      emergency_phone: user.emergency_phone ?? "",
      gender: user.gender ?? "",
      mobile_phone: user.mobile_phone ?? "",
      dob: user.dob ?? "",
      guardianName: user.guardianName ?? "",
      guardianPhone: user.guardianPhone ?? "",
      address2: user.address2 ?? null,
    },
  });

  const dob = watch("dob");
  const showGuardianFields = useMemo(() => needsGuardian(dob), [dob]);

  const updateUserMutation = useUpdateUserMutation();

  const onSubmit = handleSubmit(async (data) => {
    if (needsGuardian(data.dob)) {
      let hasError = false;
      if (!isSetAndNotEmpty(data.guardianName)) {
        setError("guardianName", { message: "Guardian Name is required" });
        hasError = true;
      }
      if (
        !isSetAndNotEmpty(data.guardianPhone) ||
        data.guardianPhone.length < 10
      ) {
        setError("guardianPhone", {
          message: "Guardian Phone is required",
        });
        hasError = true;
      }
      if (hasError) {
        return;
      }
    }

    await updateUserMutation.mutateAsync(data);

    notify(`Your profile changes have been saved`);
  });

  const { ref: placesRef } = usePlacesWidget<HTMLInputElement>({
    apiKey: import.meta.env.VITE_GOOGLE_API_KEY,
    options: {
      types: ["address"],
    },
    onPlaceSelected: (place) => {
      const results = parseGooglePlace(place);

      if (results) {
        if (placesRef.current) {
          placesRef.current.value = results.address;
        }
        setValue("address", results.address);
        setValue("city", results.city);
        setValue("state", results.state);
        setValue("postal_code", results.postal_code);
      }
    },
  });

  usePageEventTracking();

  const title =
    props.purpose === "waiver" ? PageTitle.WAIVER : PageTitle.ACCOUNT_PROFILE;
  useDocumentTitle(title);

  const guardianName = watch("guardianName");
  const guardianPhone = watch("guardianPhone");

  useEffect(() => {
    if (isSetAndNotEmpty(guardianName)) {
      clearErrors("guardianName");
    }
    if (isSetAndNotEmpty(guardianPhone)) {
      clearErrors("guardianPhone");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guardianName, guardianPhone]);

  return (
    <StandardContent>
      <form onSubmit={onSubmit}>
        <Header className="mb-6">Account details and waiver</Header>

        <FormFields className="mb-10">
          <div className="flex flex-wrap gap-4 sm:flex-nowrap">
            <FormField className="basis-full">
              <Label htmlFor="firstName">First Name</Label>
              <Input id="firstName" type="text" {...register("first_name")} />
            </FormField>

            <FormField className="basis-full">
              <Label htmlFor="lastName">Last Name</Label>
              <Input id="lastName" type="text" {...register("last_name")} />
            </FormField>
          </div>

          <FormField>
            <Label htmlFor="dob">Date of Birth</Label>
            <Input
              id="dob"
              type="date"
              min={minDOB}
              max={maxDOB}
              {...register("dob")}
            />
          </FormField>

          <FormField>
            <Label htmlFor="gender">Gender</Label>
            <Select id="gender" {...register("gender")}>
              <option></option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="None">Prefer not to say</option>
            </Select>
          </FormField>

          <FormField>
            <Label htmlFor="mobile_phone">Phone</Label>
            <Input id="mobile_phone" type="tel" {...register("mobile_phone")} />
          </FormField>

          <FormField>
            <Label htmlFor="address">Address</Label>
            <Input
              id="address"
              type="text"
              ref={placesRef}
              defaultValue={user?.address ?? ""}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setValue("address", event.target.value);
              }}
            />
          </FormField>

          <FormField>
            <Label htmlFor="address2">Address Line 2</Label>
            <Input
              id="address2"
              type="text"
              defaultValue={user?.address2 ?? ""}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                if (!isSetAndNotEmpty(event.target.value)) {
                  setValue("address2", null);
                  return;
                }
                setValue("address2", event.target.value);
              }}
              placeholder={"Apt, Suite, etc."}
            />
          </FormField>

          <FormField>
            <Label htmlFor="city">City</Label>
            <Input id="city" type="text" {...register("city")} />
          </FormField>

          <div className="flex flex-wrap gap-4 sm:flex-nowrap">
            <FormField className="basis-full">
              <Label htmlFor="state">State</Label>
              <Input id="state" type="text" {...register("state")} />
            </FormField>

            <FormField className="basis-full">
              <Label htmlFor="zip">Zip</Label>
              <Input id="zip" type="text" {...register("postal_code")} />
            </FormField>
          </div>

          <FormField>
            <Label htmlFor="emergency_name">Emergency Contact Name</Label>
            <Input
              id="emergency_name"
              type="text"
              {...register("emergency_name")}
            />
          </FormField>

          <FormField>
            <Label htmlFor="emergency_phone">Emergency Contact Phone</Label>
            <Input
              id="emergency_phone"
              type="tel"
              {...register("emergency_phone")}
            />
          </FormField>

          {showGuardianFields ? (
            <>
              <FormField>
                <Label htmlFor="guardianName">Guardian Name</Label>
                <Input
                  id="guardianName"
                  type="text"
                  {...register("guardianName")}
                />
                {errors.guardianName && (
                  <ErrorText>{errors.guardianName.message}</ErrorText>
                )}
              </FormField>

              <FormField>
                <Label htmlFor="guardianPhone">Guardian Phone</Label>
                <Input
                  id="guardianPhone"
                  type="tel"
                  {...register("guardianPhone")}
                />
                {errors.guardianPhone && (
                  <ErrorText>{errors.guardianPhone.message}</ErrorText>
                )}
              </FormField>
            </>
          ) : null}

          {updateUserMutation.isError && (
            <ErrorText error={updateUserMutation.error} />
          )}

          {updateUserMutation.isPending ? (
            <Loader />
          ) : (
            <Button type="submit" disabled={!isValid}>
              Save
            </Button>
          )}
        </FormFields>
      </form>

      <div className="flex flex-col gap-1 bg-linx-white p-4">
        {waiver.state === "VALID" ? (
          <>
            <p className="font-semibold">No Action is required</p>

            {waiver.expiresAt && (
              <p>
                Your waiver is valid until{" "}
                <strong>{format(waiver.expiresAt, "MMMM do, yyyy")}</strong>.
              </p>
            )}

            <p>No action is needed at this time.</p>

            <Button
              type="button"
              onClick={() => {
                window.open(
                  `${import.meta.env.VITE_WAIVER_URL}?pow_customer=${
                    user.guid
                  }&on_success_redirect_url=${window.location.href}`,
                  "_blank",
                );
              }}
              className="mt-2"
              disabled={hasIncompletedAccountInformation}
            >
              Update Waiver
            </Button>
          </>
        ) : (
          <>
            <p className="font-semibold">Action is required</p>

            {hasIncompletedAccountInformation ? (
              <p>
                Please complete all account information before starting waiver
              </p>
            ) : (
              <>
                {waiver.state === "NOT_SIGNED" && (
                  <>
                    <p>
                      We require you to complete a mandatory waiver before you
                      can receive any of our services.
                    </p>
                    <p>
                      Please take the time to complete a waiver prior to
                      attending your appointment.
                    </p>
                  </>
                )}

                {waiver.state === "EXPIRED" && (
                  <>
                    <p>
                      We require you to complete a mandatory waiver before you
                      can receive any of our services.
                    </p>
                    {waiver.expiresAt && (
                      <p>
                        Your waiver will expire{" "}
                        <strong>
                          {format(waiver.expiresAt, "MMMM do, yyyy")}
                        </strong>
                        .
                      </p>
                    )}
                    <p>
                      Please take the time to complete a new waiver prior to
                      attending your appointment.
                    </p>
                  </>
                )}
              </>
            )}
            <Button
              type="button"
              onClick={() => {
                // Using an onClick handler here instead of a link to use the buttons disabled state
                window.open(
                  `${import.meta.env.VITE_WAIVER_URL}?pow_customer=${
                    user.guid
                  }&on_success_redirect_url=${window.location.href}`,
                  "_blank",
                );
              }}
              className="mt-2"
              disabled={hasIncompletedAccountInformation}
            >
              Complete Waiver
            </Button>
          </>
        )}
      </div>
    </StandardContent>
  );
}
